import React, { FunctionComponent, useContext } from 'react';
import styled from 'styled-components';
import { useIntl, FormattedMessage } from 'react-intl';
import { Hyperlink, Flex } from 'mdlkit';
import { Modal } from '../../core';
import { BrandingContext } from '../app/BrandingProvider';
import IneligibleReason from './IneligibleError/IneligibleReason';
import {
  EligibilityCheckResponse,
  PatientSubmitError,
} from '../../interfaces/register';
import { V2PatientProfile } from '../../interfaces/patient';
import { REGISTRATION } from '../../constants/events';
import { event } from '../../lib/analytics';

const StyledHyperlink = styled(Hyperlink)`
  border-top: 1px solid ${({ theme }) => theme.colors.borderGray};
  width: 100%;
  margin-top: ${({ theme }) => theme.space.md}px;
  padding-top: ${({ theme }) => theme.space.md}px;
  text-align: center;
`;

interface Props {
  isOpen: boolean;
  onDismiss(): void;
  onSubmit: (values: V2PatientProfile) => Promise<void | PatientSubmitError>;
  eligibilityCheck: EligibilityCheckResponse;
  password: string;
  phone?: string;
}

const EligibilityErrorModal: FunctionComponent<Props> = ({
  isOpen,
  onDismiss,
  onSubmit,
  eligibilityCheck,
  password,
  phone,
}: Props) => {
  const { formatMessage: f } = useIntl();
  const { branding } = useContext(BrandingContext);

  const handleContinue = (): Promise<void | PatientSubmitError> => {
    const {
      email,
      firstName,
      lastName,
      gender,
      dobMonth,
      dobDay,
      dobYear,
      zip,
    } = eligibilityCheck;

    return onSubmit({
      affiliationId: branding?.affiliation.id,
      displayIneligibleError: false,
      email,
      password,
      firstName,
      lastName,
      gender,
      dobDay,
      dobMonth,
      dobYear,
      zip,
      phone,
      acceptTerms: true,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      ariaLabel={f({ id: 'eligibilityError.ariaLabel' })}
    >
      <Modal.Header pb="none">
        <FormattedMessage id="eligibilityError.heading" />
      </Modal.Header>
      <Modal.Content>
        <Flex column align="center">
          <IneligibleReason
            showContinueWithoutCoverageButton={Boolean(
              branding?.affiliation &&
                !branding.affiliation.disableIneligibleMemberRegistration
            )}
            onContinue={handleContinue}
            eligibilityCheck={eligibilityCheck}
          />
          <StyledHyperlink
            data-testid="retryLink"
            href="#"
            data-track-event
            data-track={JSON.stringify({
              controlName: 'retry-without-coverage-link',
              controlText: f({ id: 'eligibilityError.retry' }),
            })}
            onClick={(e) => {
              e.preventDefault();
              event({
                action: 'retry_eligibility_check',
                category: REGISTRATION,
              });
              onDismiss();
            }}
          >
            <FormattedMessage id="eligibilityError.retry" />
          </StyledHyperlink>
        </Flex>
      </Modal.Content>
    </Modal>
  );
};

export default EligibilityErrorModal;
